/**
 * Tudo que dependia do Electron passa por aqui. Hoje sao implementacoes de
 * navegador; quando voltarmos a empacotar como aplicativo, basta trocar o corpo
 * destas funcoes sem mexer no resto da interface.
 */

const CREDENTIALS_KEY = 'callzone:credentials';
const SETTINGS_KEY = 'callzone:settings';

export interface Credentials {
  serverUrl: string;
  token: string;
}

export function getCredentials(): Credentials | null {
  try {
    const raw = localStorage.getItem(CREDENTIALS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Credentials;
    if (!parsed?.token || !parsed?.serverUrl) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setCredentials(credentials: Credentials) {
  try {
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(credentials));
  } catch {
    // Navegacao anonima com armazenamento bloqueado: a sessao dura ate fechar a aba.
  }
}

export function clearCredentials() {
  try {
    localStorage.removeItem(CREDENTIALS_KEY);
  } catch {
    // ignora
  }
}

export function getSettings(): Record<string, unknown> {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export function setSettings(settings: Record<string, unknown>) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignora
  }
}

/** Nome usado para identificar esta sessao na lista de dispositivos. */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Navegador';
  const platform = (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform;
  return `${browser} em ${platform ?? 'desktop'}`;
}

// ------------------------------------------------------------- notificacoes

const BASE_TITLE = 'CallZone';
let unread = 0;
let titleTimer: ReturnType<typeof setInterval> | null = null;

function renderTitle() {
  document.title = unread > 0 ? `(${unread}) ${BASE_TITLE}` : BASE_TITLE;
}

/** Chama atencao para a aba quando chega mensagem com o app em segundo plano. */
export function notifyMessage(author: string, preview: string) {
  if (document.visibilityState === 'visible' && document.hasFocus()) return;

  unread++;
  renderTitle();

  if (!titleTimer) {
    // Pisca o titulo ate a pessoa voltar para a aba.
    let on = true;
    titleTimer = setInterval(() => {
      document.title = on ? `${'\u{1F4AC}'} Nova mensagem` : `(${unread}) ${BASE_TITLE}`;
      on = !on;
    }, 1200);
  }

  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification(author, { body: preview.slice(0, 140), icon: '/icon-192.png', tag: 'callzone' });
    } catch {
      // Alguns navegadores exigem Service Worker; o piscar do titulo ja resolve.
    }
  }
}

export function clearNotifications() {
  unread = 0;
  if (titleTimer) clearInterval(titleTimer);
  titleTimer = null;
  renderTitle();
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') clearNotifications();
  });
  window.addEventListener('focus', clearNotifications);
}

// ------------------------------------------------------------ compartilhar

/**
 * No navegador quem escolhe a tela e o proprio Chrome/Edge, com o seletor
 * nativo. Nao da para listar as janelas por conta propria (era o que o Electron
 * permitia), mas pedindo audio junto o seletor mostra a opcao de compartilhar
 * o som da aba ou do sistema.
 */
export function canShareScreen(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
}

/** Aviso claro quando o navegador nao permite compartilhar tela. */
export function screenShareUnavailableReason(): string | null {
  if (!window.isSecureContext) {
    return 'Compartilhar tela exige HTTPS (ou localhost). Acesse o app por um endereco seguro.';
  }
  if (!canShareScreen()) {
    return 'Este navegador nao suporta compartilhamento de tela. Use Chrome ou Edge no computador.';
  }
  return null;
}
